import { DollarSign, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { DashboardInsights } from "../../../../../../hooks/useDashboardInsights";
import { Skeleton } from "../../../../../ui/skeleton";
import { Progress } from "../../../../../ui/progress";
import { DataLabel, NoData } from "./DataLabel";
import { TooltipInfo } from "./TooltipInfo";

interface BusinessImpactProps {
  insights: DashboardInsights | null;
  loading: boolean;
}

export const BusinessImpact = ({ insights, loading }: BusinessImpactProps) => {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-border/50"><CardHeader><Skeleton className="h-4 w-32" /></CardHeader><CardContent><Skeleton className="h-10 w-24" /></CardContent></Card>
        <Card className="border-border/50"><CardHeader><Skeleton className="h-4 w-32" /></CardHeader><CardContent><Skeleton className="h-10 w-24" /></CardContent></Card>
      </div>
    );
  }

  if (!insights) return <NoData />;

  if (insights.total_reviews === 0) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-border/50 p-6"><NoData message="Collect reviews to see revenue impact estimation." /></Card>
        <Card className="border-border/50 p-6"><NoData message="Collect reviews to see your trust score." /></Card>
      </div>
    );
  }

  const trustColor =
    insights.trust_score >= 80 ? "text-emerald-600" :
    insights.trust_score >= 60 ? "text-amber-500" : "text-red-500";

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Revenue Impact */}
      <Card className="border-border/50 transition-shadow hover:shadow-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            Revenue Impact Potential
            <TooltipInfo
              title="How this is estimated"
              description={"This is an estimated impact based on your rating improvement.\n\nStudies show that a 0.1⭐ increase can lead to a 2–5% increase in revenue.\n\nThis is a directional estimate, not a guaranteed outcome."}
            />
          </CardTitle>
          <DollarSign className="h-4 w-4 text-emerald-600" />
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-2xl font-bold text-emerald-600">
              ~{insights.estimated_revenue_impact_percent.toFixed(0)}% lift
            </div>
            <DataLabel type="estimated" className="mt-1" />
            <p className="text-xs text-muted-foreground mt-1">
              Based on rating improvement (0.1⭐ ≈ 2-5% revenue lift)
            </p>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">How this is calculated</p>
            <p>Your overall rating of {insights.overall_avg_rating}⭐ with {insights.total_reviews} reviews creates trust signals that directly impact conversion rates and foot traffic. This is an industry-benchmark estimate, not a guarantee.</p>
          </div>
        </CardContent>
      </Card>

      {/* Trust Score */}
      <Card className="border-border/50 transition-shadow hover:shadow-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            Customer Trust Score
            <TooltipInfo
              title="How Trust Score is calculated"
              description={"Your Trust Score measures how trustworthy your business appears online.\n\nIt is calculated based on:\n⭐ Rating Quality (50%)\n📊 Review Volume (30%)\n😊 Positive Sentiment (20%)\n\nHigher score = stronger customer trust and better conversion potential."}
            />
          </CardTitle>
          <ShieldCheck className="h-4 w-4 text-violet-500" />
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-bold ${trustColor}`}>{insights.trust_score}</span>
            <span className="text-lg text-muted-foreground">/ 100</span>
          </div>
          <DataLabel type="derived" />
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Rating Quality</span>
              <span className="font-medium">{insights.trust_score_components.rating}%</span>
            </div>
            <Progress value={insights.trust_score_components.rating} className="h-1.5" />

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Review Volume</span>
              <span className="font-medium">{insights.trust_score_components.volume}%</span>
            </div>
            <Progress value={insights.trust_score_components.volume} className="h-1.5" />

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Positive Sentiment</span>
              <span className="font-medium">{insights.trust_score_components.sentiment}%</span>
            </div>
            <Progress value={insights.trust_score_components.sentiment} className="h-1.5" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
